import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect, useSelector } from 'react-redux';

import { normalizeURL } from '../helpers/url';
import { checkIfSSOIsEnabled } from '@wetransfer/transfer/auth-bundle/helpers/sso';
import { login as loginAction } from '@wetransfer/transfer/auth-bundle/store/actions';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import SplashpageHelper from '@wetransfer/transfer/helper/splashpage';
import { getPlanTier } from '@wetransfer/transfer/helper/user';
import Analytics from '@wetransfer/transfer/modules/analytics';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import I18n from '@wetransfer/transfer/modules/i18n';
import Router from '@wetransfer/transfer/modules/router';
import { Pill } from '@wetransfer/transfer/oneui/react';
import { Spinner } from '@wetransfer/transfer/spaces-bundle/components/Spinner/Spinner';

import {
  getPropsConsideringNavigationItemState,
  NAVIGATION_ITEM_STATE,
} from './helpers/navigation-item';
import NavigationItemBackArrow from './navigation-item-back-arrow';
import NavigationItemImage from './navigation-item-image';
import NavigationItemLabel from './navigation-item-label';

export const NavigationItem = ({
  isActive,
  preventSelfClose,
  path,
  onClick,
  children,
  isLoading,
  ...props
}) => {
  const {
    shouldBrowserHandle,
    sendUserTier,
    trackEvent,
    login,
    withBackButton,
    hasChannelName,
    name,
    image,
    hasLoadingState,
    isBetaFeature,
    ...restProps
  } = getPropsConsideringNavigationItemState(
    props,
    isActive ? NAVIGATION_ITEM_STATE.ACTIVE : NAVIGATION_ITEM_STATE.DEFAULT
  );

  const isSplashPageEnabled = SplashpageHelper.shouldDisplaySplashpage();

  const url =
    props.url || normalizeURL(path, restProps.queryParams, restProps.fragment);

  const { channel, user } = useSelector((state) => ({
    channel: state.channel,
    user: state.user,
  }));

  const isWallpaperSet = channel.wallpapers.length > 0;
  let timer;

  const onLinkClick = (event) => {
    if (
      name === 'navigation.portals' &&
      !Cookie.get(AppConfig.cookies.portalsOnboarded.name)
    ) {
      Cookie.set(AppConfig.cookies.portalsOnboarded.name, 1, { days: 365 });
    }

    if (sendUserTier) {
      Analytics.trackSnowplowEvent({
        category: 'navigation_bar',
        action: 'clickNavPricing',
        label: getPlanTier(user) || 'anonymous',
      });
    }

    if (shouldBrowserHandle) {
      if (trackEvent) {
        let label = getPlanTier(user) || 'anonymous';

        if (
          trackEvent.category === 'teams_onboarding' &&
          trackEvent.action === 'clickNavBranding'
        ) {
          label = isWallpaperSet ? 'callout_wallpaper_set' : '';
        }

        Analytics.trackSnowplowEvent({
          category: trackEvent.category,
          action: trackEvent.action,
          label: label,
        });
      }

      return;
    }

    event.preventDefault();

    if (isActive) {
      if (!preventSelfClose) {
        timer = setTimeout(
          Router.navigateTo,
          0,
          Router.getLastRoute('transfer', '/')
        );
      }
    } else if (trackEvent.category && trackEvent.action) {
      Analytics.trackSnowplowEvent({
        category: trackEvent.category,
        action: trackEvent.action,
        label: getPlanTier(user) || 'anonymous', //optional
      });
    }

    onClick();
  };

  const attrs = {
    href: url,
    target: props.target,
    onClick: onLinkClick,
  };

  if (checkIfSSOIsEnabled() && props.name === 'navigation.login') {
    attrs['data-prevent-routing'] = true;

    attrs.onClick = (event) => {
      event.preventDefault();
      Analytics.trackSnowplowEvent({
        category: 'navigation_bar',
        action: 'clickNavSignin',
      });
      login();

      return false;
    };
  }

  useEffect(() => {
    return () => {
      // prevent state update after unmount
      clearTimeout(timer);
    };
  }, [timer]);

  return (
    <React.Fragment>
      <a
        {...attrs}
        className="nav__link"
        tabIndex={isSplashPageEnabled ? '-1' : '0'}
      >
        {hasLoadingState && isLoading ? (
          <div className="nav__spinner">
            <Spinner circumference={12} />
          </div>
        ) : (
          <>
            {withBackButton && <NavigationItemBackArrow />}
            <NavigationItemLabel hasChannelName={hasChannelName}>
              {props.hasChannelName ? name : I18n.t(name)}
            </NavigationItemLabel>
            {isBetaFeature && (
              <Pill
                textContent={I18n.t('navigation.label.beta')}
                variant="sun"
                shape="square"
                size="small"
                className="nav__item--pill"
              />
            )}
            <NavigationItemImage url={image} />
          </>
        )}
      </a>
      {children}
    </React.Fragment>
  );
};

NavigationItem.propTypes = {
  isActive: PropTypes.bool.isRequired,
  preventSelfClose: PropTypes.bool,
  path: PropTypes.string,
  url: PropTypes.string,
  target: PropTypes.string,
  queryParams: PropTypes.objectOf(PropTypes.string),
  fragment: PropTypes.string,
  shouldBrowserHandle: PropTypes.bool,
  sendUserTier: PropTypes.bool,
  hasChannelName: PropTypes.bool,
  name: PropTypes.string.isRequired,
  children: PropTypes.node,
  withBackButton: PropTypes.bool,
  image: PropTypes.string,
  trackEvent: PropTypes.shape({
    category: PropTypes.string,
    action: PropTypes.string,
    label: PropTypes.string,
  }),
  login: PropTypes.func,
  onClick: PropTypes.func,
  isLoading: PropTypes.bool,
};

NavigationItem.defaultProps = {
  trackEvent: {},

  onClick: noop,
};

export default connect(null, {
  login: loginAction,
})(NavigationItem);
